import { DateTime } from "luxon";
import { startCase } from "lodash-es";

const cronDayNumberToDayString: Record<number, string> = {
  0: "sunday",
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
  7: "sunday",
};

export type ScheduleType = "DAY" | "WEEK" | "MONTH";

export type ScheduleEndType = "AFTER" | "DATE" | "NEVER";

export interface SchedulerRunnerDataData {
  interval?: number;
  times: string[];
  // WEEK uses day name strings ("monday"); MONTH uses day-of-month numbers (1).
  selectedDays?: Array<string | number>;
}

export interface SchedulerRunnerData {
  type: ScheduleType;
  data: SchedulerRunnerDataData;
}

export interface SchedulerData {
  type: ScheduleType;
  runnerData: SchedulerRunnerData;
  ends?: ScheduleEndType;
}

export interface SchedulerTypeOption {
  label: string;
  name: ScheduleType;
  value: ScheduleType;
}

export interface SchedulerEndTypeOption {
  label: string;
  name: ScheduleEndType;
  value: ScheduleEndType;
}

export const getRandomHour = (): string => {
  return `${Math.floor(Math.random() * 24)
    .toString()
    .padStart(2, "0")}:00`;
};

const randomHourForScheduler = getRandomHour();

export const DEFAULT_SCHEDULER_DATA: SchedulerData = {
  type: "DAY",
  runnerData: {
    type: "DAY",
    data: {
      interval: 1,
      times: [randomHourForScheduler],
    },
  },
};

const dayStringToCronNumber: Record<string, number> = {
  sunday: 7,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const SCHEDULE_TYPES: Record<ScheduleType, ScheduleType> = {
  DAY: "DAY",
  WEEK: "WEEK",
  MONTH: "MONTH",
};

export const schedulerTypeOptions: SchedulerTypeOption[] = [
  {
    label: "Day",
    name: SCHEDULE_TYPES.DAY,
    value: SCHEDULE_TYPES.DAY,
  },
  {
    label: "Week",
    name: SCHEDULE_TYPES.WEEK,
    value: SCHEDULE_TYPES.WEEK,
  },
  {
    label: "Month",
    name: SCHEDULE_TYPES.MONTH,
    value: SCHEDULE_TYPES.MONTH,
  },
];
export const SCHEDULE_END_TYPES: Record<ScheduleEndType, ScheduleEndType> = {
  AFTER: "AFTER",
  DATE: "DATE",
  NEVER: "NEVER",
};

export const schedulerEndTypeOptions: SchedulerEndTypeOption[] = [
  {
    label: "after",
    name: SCHEDULE_END_TYPES.AFTER,
    value: SCHEDULE_END_TYPES.AFTER,
  },
  {
    label: "on",
    name: SCHEDULE_END_TYPES.DATE,
    value: SCHEDULE_END_TYPES.DATE,
  },
  {
    label: "never",
    name: SCHEDULE_END_TYPES.NEVER,
    value: SCHEDULE_END_TYPES.NEVER,
  },
];

interface ConvertHoursToCronArgs {
  times: string[];
  withOffset?: boolean;
}

interface ConvertHoursToCronResult {
  hoursString: string;
  minutesString: string;
}

export const convertHoursToCron = ({
  times,
  withOffset = true,
}: ConvertHoursToCronArgs): ConvertHoursToCronResult => {
  const updatedTimes = times.map((time) => {
    const [hour, minutes] = time.split(":");
    let dateTime = DateTime.now().set({
      hour: Number(hour),
      minute: Number(minutes),
    });
    if (withOffset) {
      dateTime = dateTime.minus({ minutes: DateTime.now().offset });
    }

    return dateTime.toFormat("HH:mm");
  });

  const hoursString = updatedTimes.map((time) => time?.split(":")[0]).join(",");
  const minutesString = updatedTimes?.[0].split(":")[1];

  return { hoursString, minutesString };
};

interface GetDaysAfterConvertingTimeToUTCArgs {
  timeInHourAndMinutesLocalTime: string;
  daysOfWeek: number[];
}

const getDaysAfterConvertingTimeToUTC = ({
  timeInHourAndMinutesLocalTime,
  daysOfWeek,
}: GetDaysAfterConvertingTimeToUTCArgs): number[] => {
  const localTime = DateTime.fromFormat(
    timeInHourAndMinutesLocalTime,
    "HH:mm",
    {
      zone: "local",
    }
  );

  // Convert the local time to UTC
  const utcTime = localTime.minus({ minutes: localTime.offset });

  // Check if the day has changed after conversion
  let utcDaysOfWeek = daysOfWeek;
  if (localTime.weekday !== utcTime.weekday) {
    // Determine if the UTC time is ahead or behind the local time
    if (utcTime < localTime) {
      // UTC time is behind local time, so the day decreases
      // Decrement the day by one (Sunday -> Saturday)
      // since Luxon returns 0 for Sunday, we need to convert it to 7 because the cron library (cron-builder) expects 7 for Sunday
      utcDaysOfWeek = utcDaysOfWeek.map((utcDayOfWeek) => {
        const day = (utcDayOfWeek + 6) % 7;
        if (day === 0) {
          return 7;
        }
        return day;
      });
    } else {
      // UTC time is ahead of local time, so the day increases
      // Increment the day by one (Saturday -> Sunday)
      // since Luxon returns 0 for Sunday, we need to convert it to 7 because the cron library (cron-builder) expects 7 for Sunday
      utcDaysOfWeek = utcDaysOfWeek.map((utcDayOfWeek) => {
        const day = (utcDayOfWeek + 1) % 7;
        if (day === 0) {
          return 7;
        }
        return day;
      });
    }
  }

  // Return the adjusted days of the week
  return utcDaysOfWeek;
};

interface GetDaysAfterConvertingFromCronArgs {
  timeInHourAndMinutesUTC: string;
  daysOfWeek: number[];
}

const getDaysAfterConvertingFromCron = ({
  timeInHourAndMinutesUTC,
  daysOfWeek,
}: GetDaysAfterConvertingFromCronArgs): number[] => {
  const utcTime = DateTime.fromFormat(timeInHourAndMinutesUTC, "HH:mm", {
    zone: "local",
  });

  // Convert the UTC time to local
  const localTime = utcTime.plus({ minutes: utcTime.offset });

  // Check if the day has changed after conversion
  let localDaysOfWeek = daysOfWeek;
  if (localTime.weekday !== utcTime.weekday) {
    // Determine if the UTC time is ahead or behind the local time
    if (utcTime < localTime) {
      // UTC time is behind local time, so the day decreases
      // Decrement the day by one (Sunday -> Saturday)
      localDaysOfWeek = daysOfWeek.map(
        (utcDayOfWeek) => (utcDayOfWeek + 1) % 7
      );
    } else {
      // UTC time is ahead of local time, so the day increases
      // Increment the day by one (Saturday -> Sunday)
      localDaysOfWeek = daysOfWeek.map(
        (utcDayOfWeek) => (utcDayOfWeek + 6) % 7
      );
    }
  }

  // Return the adjusted days of the week
  return localDaysOfWeek;
};

interface ConvertToCronArgs {
  schedulerData: SchedulerData;
  withOffset?: boolean;
}

export const convertToCron = ({
  schedulerData,
  withOffset = true,
}: ConvertToCronArgs): string => {
  const {
    type,
    runnerData: { data },
  } = schedulerData;

  const { hoursString, minutesString } = convertHoursToCron({
    times: data.times,
    withOffset,
  });

  const { hoursString: hoursStringLocal, minutesString: minutesStringLocal } =
    convertHoursToCron({
      times: data.times,
      withOffset: false,
    });

  switch (type) {
    case SCHEDULE_TYPES.DAY:
      return `${minutesString} ${hoursString} * * *`;
    case SCHEDULE_TYPES.WEEK:
      const daysAfterUTCConversion = getDaysAfterConvertingTimeToUTC({
        timeInHourAndMinutesLocalTime: `${hoursStringLocal}:${minutesStringLocal}`,
        daysOfWeek:
          data.selectedDays?.map((day) => dayStringToCronNumber[day]) ?? [],
      });
      return `${minutesString} ${hoursString} * * ${withOffset ? daysAfterUTCConversion : data.selectedDays?.map((day) => dayStringToCronNumber[day])}`;
    case SCHEDULE_TYPES.MONTH:
      return `${minutesStringLocal} ${hoursStringLocal} ${data.selectedDays} * *`;
    default:
      return "";
  }
};

interface GetTypeFromCronArgs {
  cron: string;
}

export const getTypeFromCron = ({
  cron,
}: GetTypeFromCronArgs): ScheduleType | undefined => {
  const [, , dayOfMonth, month, dayOfWeek] = cron.split(" ");
  if (
    dayOfMonth === "*" &&
    month === "*" &&
    (dayOfWeek === "?" || dayOfWeek === "*")
  ) {
    return SCHEDULE_TYPES.DAY;
  } else if (
    (dayOfMonth === "?" || dayOfMonth === "*") &&
    month === "*" &&
    dayOfWeek !== "?" &&
    dayOfWeek !== "*"
  ) {
    return SCHEDULE_TYPES.WEEK;
  } else if (
    dayOfMonth !== "?" &&
    dayOfMonth !== "*" &&
    month === "*" &&
    (dayOfWeek === "?" || dayOfWeek === "*")
  ) {
    return SCHEDULE_TYPES.MONTH;
  }
};

interface GetTimesFromCronArgs {
  cron: string;
  withOffset?: boolean;
}

export const getTimesFromCron = ({
  cron,
  withOffset,
}: GetTimesFromCronArgs): string[] => {
  const [minutes, allHours] = cron.split(" ");
  const hoursArr = allHours.split(",");
  return hoursArr.map((hour) => {
    let dateTime = DateTime.now().set({
      hour: Number(hour),
      minute: Number(minutes),
    });
    if (withOffset) {
      dateTime = dateTime.plus({ minutes: DateTime.now().offset });
    }
    return dateTime.toFormat("HH:mm");
  });
};

interface GetRunnerDataArgs {
  cron: string;
  type?: ScheduleType;
  withOffset?: boolean;
}

export const getRunnerData = ({
  cron,
  type,
  withOffset,
}: GetRunnerDataArgs): SchedulerRunnerData | "Invalid Date" | undefined => {
  const [, , daysOfMonth, , daysOfWeek] = cron.split(" ");
  try {
    let times = getTimesFromCron({ cron, withOffset });
    const utcTimes = getTimesFromCron({ cron, withOffset: false });

    if (type === SCHEDULE_TYPES.DAY) {
      return {
        type: SCHEDULE_TYPES.DAY,
        data: {
          times,
          interval: 1,
        },
      };
    } else if (type === SCHEDULE_TYPES.WEEK) {
      const daysAfterConversion = getDaysAfterConvertingFromCron({
        timeInHourAndMinutesUTC: utcTimes[0],
        daysOfWeek: daysOfWeek.split(",").map(Number),
      });
      return {
        type: SCHEDULE_TYPES.WEEK,
        data: {
          times,
          interval: 1,
          selectedDays: daysAfterConversion?.map(
            (day) => cronDayNumberToDayString[day]
          ),
        },
      };
    } else if (type === SCHEDULE_TYPES.MONTH) {
      return {
        type: SCHEDULE_TYPES.MONTH,
        data: {
          times: utcTimes,
          selectedDays: daysOfMonth.split(",").map(Number),
        },
      };
    }
  } catch (e) {
    return "Invalid Date";
  }
};

interface ConvertCronToSchedulerDataArgs {
  cron?: string;
  withOffset?: boolean;
}

export const convertCronToSchedulerData = ({
  cron,
  withOffset,
}: ConvertCronToSchedulerDataArgs): {
  type: ScheduleType | undefined;
  runnerData: SchedulerRunnerData | "Invalid Date" | undefined;
} => {
  const schedulerType = getTypeFromCron({ cron: cron?.trim() ?? "" });
  const runnerData = getRunnerData({
    cron: cron ?? "",
    type: schedulerType,
    withOffset,
  });
  return {
    type: schedulerType,
    runnerData: runnerData,
  };
};

const getDaySuffix = (day: string): string => {
  switch (day) {
    case "1":
    case "21":
    case "31":
      return `${day}st`;

    case "2":
    case "22":
      return `${day}nd`;

    case "3":
    case "23":
      return `${day}rd`;

    default:
      return `${day}th`;
  }
};

interface ConvertCronToTextArgs {
  cron?: string;
}

export const convertCronToText = ({
  cron,
}: ConvertCronToTextArgs): string | undefined => {
  if (!cron) {
    return "";
  }

  const [minutes, allHours, daysOfMonth, , daysOfWeek] = cron.split(" ");
  const hours = allHours.split(",").map((hour) => `${hour}:${minutes}`);
  try {
    let times = getTimesFromCron({ cron, withOffset: true });

    if (daysOfMonth !== "*" && daysOfMonth !== "?") {
      return `Every Month on ${daysOfMonth
        .split(",")
        ?.map((day) => getDaySuffix(day))
        ?.join(", ")} at 00:00 (UTC)`;
    } else if (daysOfWeek !== "*" && daysOfWeek !== "?") {
      const daysAfterConversion = getDaysAfterConvertingFromCron({
        timeInHourAndMinutesUTC: hours[0],
        daysOfWeek: daysOfWeek.split(",").map(Number),
      });
      return `Every Week on ${daysAfterConversion
        ?.map((day) => startCase(cronDayNumberToDayString[day]))
        ?.join(", ")} at ${times[0]}`;
    } else if (allHours || minutes) {
      return `Every Day at ${times.join(", ")}`;
    }
  } catch (e) {
    return "Invalid Date";
  }
};

interface ConvertSchedulerDataToTextArgs {
  data?: SchedulerData;
}

export const convertSchedulerDataToText = ({
  data,
}: ConvertSchedulerDataToTextArgs): string | undefined => {
  if (!data) {
    return "";
  }

  const { type, runnerData: { data: { times, selectedDays } = {} } = {} } =
    data;
  if (type === SCHEDULE_TYPES.MONTH) {
    return `Every Month on ${selectedDays
      ?.map((day) => getDaySuffix(String(day)))
      ?.join(", ")} at ${times?.[0]} (UTC)`;
  } else if (type === SCHEDULE_TYPES.WEEK) {
    return `Every Week on ${selectedDays
      ?.map((day) => startCase(String(day)))
      ?.join(", ")} at ${times?.[0]}`;
  } else if (type === SCHEDULE_TYPES.DAY) {
    return `Every Day at ${times?.join(", ")}`;
  }
};
